/**
 * BlogSearchAutocomplete
 * ──────────────────────
 * Full-width search input with two distinct dropdown states:
 *
 * 1. IDLE (focused, query < 2 chars)
 *    - "Recent Searches" - up to 5 queries saved in localStorage
 *    - "Trending Topics" - top 6 tags by post count
 *
 * 2. ACTIVE (query ≥ 2 chars, debounced 150 ms)
 *    - "Articles" - up to 6 fuzzy-matched blog titles/excerpts (Fuse.js)
 *    - "Topics"   - up to 4 fuzzy-matched tag names
 *
 * Fuzzy search tolerates minor typos via Fuse.js (threshold 0.35).
 * Matched character ranges are highlighted in amber.
 *
 * Keyboard: ArrowUp/Down to move, Enter to select, Escape to close.
 * Saves every submitted query to localStorage (max 5, deduped).
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  trackBlogSearchOpen,
  trackBlogSearchSuggestionClick,
  trackBlogSearchSubmit,
  trackBlogSearchHistoryCleared,
} from "@/lib/analytics";
import { useLocation } from "wouter";
import Fuse from "fuse.js";
import {
  Search,
  Tag,
  FileText,
  ArrowRight,
  Clock,
  TrendingUp,
  X,
} from "lucide-react";
import { blogPosts, TAGS } from "@/data/blogPosts";

// ── localStorage helpers ──────────────────────────────────────────────────────
const LS_KEY = "blog_recent_searches";
const MAX_RECENT = 5;

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function saveRecent(query: string) {
  try {
    const prev = loadRecent().filter(
      (q) => q.toLowerCase() !== query.toLowerCase()
    );
    const next = [query, ...prev].slice(0, MAX_RECENT);
    localStorage.setItem(LS_KEY, JSON.stringify(next));
  } catch {}
}

function removeRecent(query: string) {
  try {
    const next = loadRecent().filter((q) => q !== query);
    localStorage.setItem(LS_KEY, JSON.stringify(next));
  } catch {}
}

// ── Trending topics: top 6 tags by post count ────────────────────────────────
const TRENDING_TOPICS: string[] = (() => {
  const counts: Record<string, number> = {};
  TAGS.forEach((t) => {
    counts[t] = blogPosts.filter((p) => p.tags.includes(t)).length;
  });
  return [...TAGS]
    .sort((a, b) => (counts[b] ?? 0) - (counts[a] ?? 0))
    .slice(0, 6);
})();

// ── Fuse.js instances (created once, outside component) ──────────────────────
interface ArticleRecord {
  slug: string;
  title: string;
  excerpt: string;
  tags: string[];
}

const articleIndex: ArticleRecord[] = blogPosts.map((p) => ({
  slug: p.slug,
  title: p.title,
  excerpt: p.excerpt ?? "",
  tags: p.tags,
}));

const fuseArticles = new Fuse(articleIndex, {
  keys: [
    { name: "title", weight: 0.7 },
    { name: "excerpt", weight: 0.2 },
    { name: "tags", weight: 0.1 },
  ],
  threshold: 0.35,          // 0 = exact, 1 = match anything - 0.35 tolerates ~2 typos
  distance: 200,            // allow matches anywhere in the string
  includeScore: true,
  includeMatches: true,
  ignoreLocation: true,
  minMatchCharLength: 2,
});

const fuseTags = new Fuse(TAGS, {
  threshold: 0.35,
  includeScore: true,
  includeMatches: true,
  minMatchCharLength: 2,
});

// ── Highlight helper using Fuse match indices ─────────────────────────────────
function highlightFuse(
  text: string,
  indices: ReadonlyArray<[number, number]> | undefined
): React.ReactNode {
  if (!indices || indices.length === 0) return text;

  const parts: React.ReactNode[] = [];
  let cursor = 0;

  // Fuse indices can overlap; sort and merge them
  const merged: [number, number][] = [];
  const sorted = [...indices].sort((a, b) => a[0] - b[0]);
  for (const [s, e] of sorted) {
    if (merged.length && s <= merged[merged.length - 1][1] + 1) {
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], e);
    } else {
      merged.push([s, e]);
    }
  }

  for (const [start, end] of merged) {
    if (cursor < start) parts.push(text.slice(cursor, start));
    parts.push(
      <mark
        key={start}
        className="bg-amber-400 text-gray-900 rounded-sm px-0.5"
      >
        {text.slice(start, end + 1)}
      </mark>
    );
    cursor = end + 1;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <>{parts}</>;
}

// Fallback plain highlight (for recent searches re-run)
function highlightPlain(text: string, query: string): React.ReactNode {
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-amber-400 text-gray-900 rounded-sm px-0.5">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

// ── Types ─────────────────────────────────────────────────────────────────────
type SuggestionKind = "article" | "tag" | "recent" | "trending";

interface Suggestion {
  kind: SuggestionKind;
  label: string;
  slug?: string;
  tag?: string;
  excerpt?: string;
  /** Fuse match indices for the title field */
  titleIndices?: ReadonlyArray<[number, number]>;
  /** Fuse match indices for the tag/label field */
  labelIndices?: ReadonlyArray<[number, number]>;
}

// ── Props ─────────────────────────────────────────────────────────────────────
interface Props {
  onSearch: (query: string) => void;
  onTagSelect: (tag: string) => void;
  placeholder?: string;
  className?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function BlogSearchAutocomplete({
  onSearch,
  onTagSelect,
  placeholder = "Search articles - AEO, Agentic AI, Google AI Mode, B2B GTM…",
  className = "",
}: Props) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [, setLocation] = useLocation();

  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Load recent searches on mount
  useEffect(() => {
    setRecentSearches(loadRecent());
  }, []);

  // ── Debounced query ──────────────────────────────────────────────────────────
  const [debouncedQuery, setDebouncedQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 150);
    return () => clearTimeout(t);
  }, [query]);

  const isIdle = debouncedQuery.trim().length < 2;

  // ── Fuzzy suggestions (active mode) ─────────────────────────────────────────
  const activeSuggestions = useMemo<Suggestion[]>(() => {
    if (isIdle) return [];
    const q = debouncedQuery.trim();

    const articleResults = fuseArticles.search(q, { limit: 6 });
    const articles: Suggestion[] = articleResults.map((r) => {
      const titleMatch = r.matches?.find((m) => m.key === "title");
      return {
        kind: "article" as const,
        label: r.item.title,
        slug: r.item.slug,
        excerpt: r.item.excerpt,
        titleIndices: titleMatch?.indices as ReadonlyArray<[number, number]> | undefined,
      };
    });

    const tagResults = fuseTags.search(q, { limit: 4 });
    const tags: Suggestion[] = tagResults.map((r) => ({
      kind: "tag" as const,
      label: r.item,
      tag: r.item,
      labelIndices: r.matches?.[0]?.indices as ReadonlyArray<[number, number]> | undefined,
    }));

    return [...articles, ...tags];
  }, [debouncedQuery, isIdle]);

  // ── Idle suggestions ─────────────────────────────────────────────────────────
  const idleSuggestions = useMemo<Suggestion[]>(() => {
    if (!isIdle) return [];
    const recent: Suggestion[] = recentSearches.map((q) => ({
      kind: "recent" as const,
      label: q,
    }));
    const trending: Suggestion[] = TRENDING_TOPICS.map((t) => ({
      kind: "trending" as const,
      label: t,
      tag: t,
    }));
    return [...recent, ...trending];
  }, [isIdle, recentSearches]);

  const suggestions = isIdle ? idleSuggestions : activeSuggestions;

  // Reset active index when suggestions change
  useEffect(() => {
    setActiveIdx(-1);
  }, [debouncedQuery]);

  // ── Outside-click close ──────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Scroll active item into view ─────────────────────────────────────────────
  useEffect(() => {
    if (activeIdx >= 0 && listRef.current) {
      const item = listRef.current.children[activeIdx] as
        | HTMLElement
        | undefined;
      item?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIdx]);

  // ── Select a suggestion ──────────────────────────────────────────────────────
  const selectSuggestion = useCallback(
    (s: Suggestion, idx: number) => {
      setOpen(false);
      setActiveIdx(-1);
      trackBlogSearchSuggestionClick({
        suggestion_type: s.kind,
        suggestion_label: s.label,
        query_at_click: query,
        position: idx,
      });
      if (s.kind === "article" && s.slug) {
        saveRecent(s.label);
        setRecentSearches(loadRecent());
        setQuery(s.label);
        setLocation(`/blog/${s.slug}/`);
      } else if (s.kind === "tag" || s.kind === "trending") {
        setQuery("");
        onTagSelect(s.tag!);
      } else if (s.kind === "recent") {
        setQuery(s.label);
        saveRecent(s.label);
        setRecentSearches(loadRecent());
        onSearch(s.label);
      }
    },
    [query, setLocation, onTagSelect, onSearch]
  );

  // ── Keyboard handler ─────────────────────────────────────────────────────────
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!open) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, -1));
      } else if (e.key === "Escape") {
        setOpen(false);
        setActiveIdx(-1);
      } else if (e.key === "Enter" && activeIdx >= 0) {
        e.preventDefault();
        selectSuggestion(suggestions[activeIdx], activeIdx);
      }
    },
    [open, activeIdx, suggestions, selectSuggestion]
  );

  // ── Form submit ──────────────────────────────────────────────────────────────
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setOpen(false);
    saveRecent(q);
    setRecentSearches(loadRecent());
    trackBlogSearchSubmit({
      search_query: q,
      result_count: activeSuggestions.length,
      input_method: "button_click",
    });
    onSearch(q);
  };

  // ── Remove a recent search ───────────────────────────────────────────────────
  const handleRemoveRecent = (e: React.MouseEvent, q: string) => {
    e.stopPropagation();
    removeRecent(q);
    setRecentSearches(loadRecent());
  };

  // ── Grouping for render ──────────────────────────────────────────────────────
  const recentItems = suggestions.filter((s) => s.kind === "recent");
  const trendingItems = suggestions.filter((s) => s.kind === "trending");
  const articleItems = suggestions.filter((s) => s.kind === "article");
  const tagItems = suggestions.filter((s) => s.kind === "tag");

  // Flat index offsets for keyboard nav
  const trendingOffset = recentItems.length;
  const tagOffset = articleItems.length; // active mode only

  const showDropdown = open && suggestions.length > 0;

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <form onSubmit={handleSubmit} role="search" aria-label="Search blog articles">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10" />
        <input
          ref={inputRef}
          type="search"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-controls="blog-autocomplete-list"
          aria-activedescendant={
            activeIdx >= 0 ? `suggestion-${activeIdx}` : undefined
          }
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setOpen(true);
            trackBlogSearchOpen({
              trigger_source: "focus",
              page_path: typeof window !== "undefined" ? window.location.pathname : "/blog",
            });
          }}
          autoComplete="off"
          spellCheck={false}
          className="w-full pl-11 pr-28 h-12 text-sm md:text-base rounded-xl border border-border bg-background focus:bg-background focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all"
        />
        <button
          type="submit"
          className="absolute right-3 top-1/2 -translate-y-1/2 px-4 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 active:scale-95 text-background text-sm font-semibold transition-all duration-150"
        >
          Search
        </button>
      </form>

      {/* ── Dropdown ─────────────────────────────────────────────────────────── */}
      {showDropdown && (
        <div
          className="absolute left-0 right-0 top-[calc(100%+6px)] z-50 rounded-xl border border-border bg-card shadow-2xl overflow-hidden"
          style={{ maxHeight: "460px", overflowY: "auto" }}
        >
          <ul
            id="blog-autocomplete-list"
            ref={listRef}
            role="listbox"
            aria-label="Search suggestions"
          >
            {/* ── IDLE MODE ─────────────────────────────────────────────────── */}
            {isIdle && (
              <>
                {/* Recent Searches */}
                {recentItems.length > 0 && (
                  <>
                    <li className="px-4 pt-3 pb-1 flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        Recent Searches
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          localStorage.removeItem(LS_KEY);
                          setRecentSearches([]);
                        }}
                        className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                      >
                        Clear all
                      </button>
                    </li>
                    {recentItems.map((s, i) => (
                      <li
                        key={`recent-${i}`}
                        id={`suggestion-${i}`}
                        role="option"
                        aria-selected={activeIdx === i}
                        onMouseEnter={() => setActiveIdx(i)}
                        onMouseLeave={() => setActiveIdx(-1)}
                        onClick={() => selectSuggestion(s, i)}
                        className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors duration-100 ${
                          activeIdx === i
                            ? "bg-amber-500/10"
                            : "hover:bg-muted/60"
                        }`}
                      >
                        <Clock className="w-3.5 h-3.5 text-muted-foreground/50 shrink-0" />
                        <span className="flex-1 text-sm text-foreground">
                          {s.label}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => handleRemoveRecent(e, s.label)}
                          className="p-0.5 rounded hover:bg-muted text-muted-foreground/50 hover:text-foreground transition-colors"
                          aria-label={`Remove "${s.label}" from recent searches`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </li>
                    ))}
                  </>
                )}

                {/* Trending Topics */}
                {trendingItems.length > 0 && (
                  <>
                    <li
                      className={`px-4 pb-1 ${
                        recentItems.length > 0
                          ? "pt-3 border-t border-border"
                          : "pt-3"
                      }`}
                    >
                      <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                        <TrendingUp className="w-3 h-3" />
                        Trending Topics
                      </span>
                    </li>
                    <li className="px-4 pb-3">
                      <div className="flex flex-wrap gap-2 pt-1">
                        {trendingItems.map((s, i) => {
                          const flatIdx = trendingOffset + i;
                          return (
                            <button
                              key={`trending-${i}`}
                              id={`suggestion-${flatIdx}`}
                              type="button"
                              role="option"
                              aria-selected={activeIdx === flatIdx}
                              onMouseEnter={() => setActiveIdx(flatIdx)}
                              onMouseLeave={() => setActiveIdx(-1)}
                              onClick={() => selectSuggestion(s, flatIdx)}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-150 border ${
                                activeIdx === flatIdx
                                  ? "bg-amber-500/20 border-amber-500/50 text-amber-400"
                                  : "bg-muted/50 border-border text-muted-foreground hover:bg-amber-500/10 hover:border-amber-500/30 hover:text-amber-500"
                              }`}
                            >
                              {s.label}
                            </button>
                          );
                        })}
                      </div>
                    </li>
                  </>
                )}
              </>
            )}

            {/* ── ACTIVE MODE (fuzzy results) ───────────────────────────────── */}
            {!isIdle && (
              <>
                {/* Articles */}
                {articleItems.length > 0 && (
                  <>
                    <li className="px-4 pt-3 pb-1">
                      <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                        <FileText className="w-3 h-3" />
                        Articles
                      </span>
                    </li>
                    {articleItems.map((s, i) => (
                      <li
                        key={`article-${i}`}
                        id={`suggestion-${i}`}
                        role="option"
                        aria-selected={activeIdx === i}
                        onMouseEnter={() => setActiveIdx(i)}
                        onMouseLeave={() => setActiveIdx(-1)}
                        onClick={() => selectSuggestion(s, i)}
                        className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors duration-100 ${
                          activeIdx === i
                            ? "bg-amber-500/10"
                            : "hover:bg-muted/60"
                        }`}
                      >
                        <ArrowRight
                          className={`w-3.5 h-3.5 mt-0.5 shrink-0 transition-colors ${
                            activeIdx === i
                              ? "text-amber-500"
                              : "text-muted-foreground/40"
                          }`}
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground leading-snug line-clamp-1">
                            {s.titleIndices
                              ? highlightFuse(s.label, s.titleIndices)
                              : highlightPlain(s.label, debouncedQuery)}
                          </p>
                          {s.excerpt && (
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                              {s.excerpt.slice(0, 100)}
                            </p>
                          )}
                        </div>
                      </li>
                    ))}
                  </>
                )}

                {/* Tags */}
                {tagItems.length > 0 && (
                  <>
                    <li
                      className={`px-4 pb-1 ${
                        articleItems.length > 0
                          ? "pt-3 border-t border-border"
                          : "pt-3"
                      }`}
                    >
                      <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                        <Tag className="w-3 h-3" />
                        Topics
                      </span>
                    </li>
                    {tagItems.map((s, i) => {
                      const flatIdx = tagOffset + i;
                      return (
                        <li
                          key={`tag-${i}`}
                          id={`suggestion-${flatIdx}`}
                          role="option"
                          aria-selected={activeIdx === flatIdx}
                          onMouseEnter={() => setActiveIdx(flatIdx)}
                          onMouseLeave={() => setActiveIdx(-1)}
                          onClick={() => selectSuggestion(s, flatIdx)}
                          className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors duration-100 ${
                            activeIdx === flatIdx
                              ? "bg-amber-500/10"
                              : "hover:bg-muted/60"
                          }`}
                        >
                          <span
                            className={`text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${
                              activeIdx === flatIdx
                                ? "bg-amber-500/20 text-amber-400"
                                : "bg-amber-500/10 text-amber-600"
                            }`}
                          >
                            {s.labelIndices
                              ? highlightFuse(s.label, s.labelIndices)
                              : highlightPlain(s.label, debouncedQuery)}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            Browse all articles →
                          </span>
                        </li>
                      );
                    })}
                  </>
                )}

                {/* No results */}
                {activeSuggestions.length === 0 && (
                  <li className="px-4 py-6 text-center">
                    <p className="text-sm text-muted-foreground">
                      No articles found for &ldquo;{debouncedQuery}&rdquo;
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        onSearch(debouncedQuery);
                      }}
                      className="mt-2 text-xs text-amber-600 hover:text-amber-500 font-medium"
                    >
                      Search anyway →
                    </button>
                  </li>
                )}

                {/* Footer hint */}
                {activeSuggestions.length > 0 && (
                  <li className="px-4 py-2.5 border-t border-border bg-muted/30">
                    <p className="text-[11px] text-muted-foreground">
                      Press{" "}
                      <kbd className="px-1 py-0.5 rounded bg-border text-foreground text-[10px] font-mono">
                        ↵
                      </kbd>{" "}
                      to search all articles for &ldquo;{query}&rdquo;
                    </p>
                  </li>
                )}
              </>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
